import type { ICUParams } from "@v0y4ge/i18n-runtime";
import { currentLocale, useT } from "../../lib/i18n";
import type { DashboardStat } from "@v0y4ge/api-contracts";

type Translate = ReturnType<typeof useT>;

/** Stable labelCode/delta.code -> i18n catalog key mapping (backend-i18n Phase 2,
 * #134, mirrors the Store Ops clienteling pilot / storeops-i18n.ts). The worker
 * (workers/events/src/routes/studio-dashboard.ts) and the mocks
 * (packages/api-mocks/src/seeds/{dashboard,cd-demand}.seed.ts) emit these exact
 * snake_case codes; this is the ONLY place the cd-app maps them to catalog keys.
 *
 * KIT-DRIFT SHIM (2026-07-22, see changelog/2026-07-22T1900-*): this doc's kit
 * (last delta 918fd872) predates #134 — its DashboardStat still types `delta`
 * as the deprecated composed string and carries no labelCode/currencyCode, and
 * the api-client's `schema.parse` chokepoint strips unknown response fields, so
 * the new fields cannot reach the app until the kit ships them. Until then the
 * #134 shapes are typed locally (LocalizedDashboardStat), labelCode falls back
 * to snake_case(stat.id) — the id values are the documented enum source — with
 * the server's composed `label` as the t() fallback, and a string `delta`
 * renders verbatim. The field reads take over with zero app changes when the
 * kit delta lands (REQ-053).
 */
export type DashboardStatLabelCode =
  | "events_today" | "registered_today" | "checked_in_today" | "event_revenue" | "check_in_rate";
export type DashboardStatDeltaCode =
  | "upcoming_this_week" | "across_events" | "pct_of_expected" | "across_active_events" | "total_capacity"
  | "pct_of_capacity" | "pct_of_registered" | "checked_in_of_registered" | "live_events_count";
/** DashboardStat + the #134 fields (delta params typed as the runtime's
 * ICUParams — every shipped delta param is numeric; the contract's `boolean`
 * member has no ICU consumer). Plain-assignable from the kit's DashboardStat. */
export type LocalizedDashboardStat = Omit<DashboardStat, "delta"> & {
  labelCode?: DashboardStatLabelCode;
  delta: string | { code: DashboardStatDeltaCode; params: ICUParams };
  currencyCode?: string;
};

export const dashboardStatLabelKey = (
  code: DashboardStatLabelCode,
): `dashboard.summary.label.${DashboardStatLabelCode}` => `dashboard.summary.label.${code}`;

const LABEL_CODES: ReadonlySet<string> = new Set([
  "events_today", "registered_today", "checked_in_today", "event_revenue", "check_in_rate",
]); // ui-config

export function renderStatLabel(stat: DashboardStat, t: Translate): string {
  const s: LocalizedDashboardStat = stat;
  const derived = stat.id.replace(/-/g, "_");
  const code = s.labelCode ?? (LABEL_CODES.has(derived) ? (derived as DashboardStatLabelCode) : undefined);
  return code ? t(dashboardStatLabelKey(code), stat.label) : stat.label;
}

// The only delta codes with no interpolated params ship as a plain catalog
// key (no `.icu` suffix); every parameterized delta is an ICU key.
const PLAIN_DELTA_CODES: ReadonlySet<string> = new Set(["across_active_events"]); // ui-config

export function renderStatDelta(stat: DashboardStat, t: Translate): string {
  const s: LocalizedDashboardStat = stat;
  const d = s.delta;
  if (typeof d === "string") return d; // deprecated composed form — pre-#134 kit data
  const suffix = PLAIN_DELTA_CODES.has(d.code) ? "" : ".icu";
  return t(`dashboard.summary.delta.${d.code}${suffix}`, d.params);
}

/** Money-valued stats (currently only `event-revenue`) carry `rawValue` +
 * `currencyCode` — format them via Intl in the active locale instead of
 * parsing the backend's pre-formatted `value` string (kept only as a
 * fallback for stats that never carry a currencyCode). */
export function formatStatValue(stat: DashboardStat, locale = currentLocale()): string {
  const s: LocalizedDashboardStat = stat;
  if (!s.currencyCode) return stat.value;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: s.currencyCode,
      currencyDisplay: "narrowSymbol",
      notation: "compact",
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    }).format(stat.rawValue);
  } catch {
    return stat.value;
  }
}
